import { ArrowDropdown } from '@/components/icons'
import { timeZoneList } from '@/constants/Timezones'
import { TimeZoneOption } from '@/types'
import { Combobox, Transition } from '@headlessui/react'
import { ChangeEvent, FC, Fragment, useRef, useState } from 'react'

interface Props {
  label?: string
  labelClassName?: string
  placeholder?: string
  value?: TimeZoneOption
  onChange: (timeZone: string, zoneName: string) => void
  onBlur: (event: any) => void
  error?: string
}

export const TimezoneSelect: FC<Props> = ({
  label = 'Time Zone',
  labelClassName,
  error,
  value = null, // default value to remove warning
  onChange,
  onBlur,
  placeholder = 'Please select the timezone',
}) => {
  const ref = useRef<HTMLInputElement>(null)

  const [searchText, setSearchText] = useState<string>('')

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setSearchText(value)
  }

  const filteredTimeZones =
    searchText === ''
      ? timeZoneList
      : timeZoneList.filter((timeZoneOpt) =>
          timeZoneOpt.label.toLowerCase().replace(/\s+/g, '').includes(searchText.toLowerCase().replace(/\s+/g, ''))
        )

  return (
    <div className={`w-full flex flex-col gap-y-2`}>
      {label && <p className={`text-white text-sm font-semibold ${labelClassName && labelClassName}`}>{label}</p>}

      <div className="relative">
        <Combobox
          value={value}
          onChange={(e: any) => {
            onChange(e, timeZoneList.find((item) => item.value === e)?.label || '')
          }}
        >
          <Combobox.Button
            onClick={() => {
              ref.current?.select()
            }}
            className={`p-1 md:p-[7px] focus:outline-none border border-navy-900 border-opacity-20
            text-navy-900 text-sm rounded-full w-full flex justify-between gap-x-2`}
          >
            <Combobox.Input
              ref={ref}
              className="w-full border-none placeholder:opacity-50 p-2 outline-none"
              onBlur={onBlur}
              placeholder={placeholder}
              displayValue={(item: TimeZoneOption) => item?.label}
              onInput={onInput}
            />
            <ArrowDropdown className="m-2" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setSearchText('')}
          >
            <Combobox.Options
              className={`absolute w-full border-t border-navy-900 border-opacity-20 bg-white z-20 mt-1
                top-full cursor-pointer shadow-sm shadow-slate-500 max-h-60 overflow-auto`}
            >
              {searchText ? (
                filteredTimeZones.length === 0 ? (
                  <div className="relative cursor-default select-none p-2">Nothing found.</div>
                ) : (
                  filteredTimeZones.map((option) => {
                    return (
                      <Combobox.Option
                        className={`p-2 hover:text-white hover:bg-primary-300 ${
                          option?.value === value?.value && 'text-white bg-primary-300'
                        }`}
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Combobox.Option>
                    )
                  })
                )
              ) : (
                timeZoneList.map((option) => {
                  return (
                    <Combobox.Option
                      className={`p-2 hover:text-white hover:bg-primary-300 ${
                        option?.value === value?.value && 'text-white bg-primary-300'
                      }`}
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Combobox.Option>
                  )
                })
              )}
            </Combobox.Options>
          </Transition>
        </Combobox>
      </div>

      {error && <div className="text-boldOrange -mt-1 font-medium">{error}</div>}
    </div>
  )
}
